import * as Joi from 'joi';
import { Moment } from 'moment';
import { Column, Entity, OneToMany, OneToOne } from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import Base from '../base.entity';
import CmChangePicture from '../cm-change-pictures/cm-change-picture.entity';
import CmEpisodicItemPicture from '../cm-episodic-item-pictures/cm-episodic-item-picture.entity';
import { DEPARTMENTS } from '../enums/enums';
import EpisodicActorPicture from '../episodic-actor-pictures/episodic-actor-picture.entity';
import EpisodicCharacterPicture from '../episodic-character-pictures/episodic-character-picture.entity';
import EpisodicLogoPicture from '../episodic-logo-pictures/episodic-logo-picture.entity';
import HaLookPicture from '../ha-look-pictures/ha-look-picture.entity';
import MoneyTrackingPaymentPicture from '../money-tracking-payment-pictures/money-tracking-payment-picture.entity';
import MuLookPicture from '../mu-look-pictures/mu-look-picture.entity';
import PdMessagePicture from '../pd-message-pictures/pd-message-pictures.entity';
import PictureScene from '../picture-scenes/picture-scene.entity';
import PrEpisodicPropPicture from '../pr-episodic-prop-pictures/pr-episodic-prop-picture.entity';
import SetMoneyTrackingPaymentPicture from '../set-money-tracking-payment-pictures/set-money-tracking-picture.entity';
import SetPicture from '../set-pictures/set-picture.entity';
import SetPiecePicture from '../set-piece-pictures/set-piece-picture.entity';

@Entity('pictures')
export default class Picture extends Base {
	static readonly tableName = 'pictures';

	static THUMBNAIL_SIZE = 150;
	static PREVIEW_SIZE = 800;
	static FULL_SIZE = 1;
	static ALL_SIZES = ['150', '400', '800'];

	@Column('varchar')
	mobile_id: string;

	@Column('int')
	prod_id: number;

	@Column('int')
	episodic_id: number;

	@Column('varchar')
	file_name: string;

	@Column('varchar')
	description: string;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	fitting_research: boolean;

	@Column('int')
	department: DEPARTMENTS;

	@Column('int')
	resize_attempts: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('int')
	last_updated_user: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@Column('int')
	created_by: number;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	needs_upload: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	old_model_delete: boolean;

	@OneToMany(
		(type) => CmEpisodicItemPicture,
		(cmEpisodicItemPicture) => cmEpisodicItemPicture.picture
	)
	cmEpisodicItemPictures: CmEpisodicItemPicture[];

	@OneToMany((type) => PictureScene, (pictureScene) => pictureScene.picture)
	pictureScenes: PictureScene[];

	@OneToMany((type) => SetPicture, (setPicture) => setPicture.picture)
	setPictures: SetPicture[];

	@OneToOne(
		(type) => EpisodicCharacterPicture,
		(episodicCharacterPicture) => episodicCharacterPicture.picture
	)
	episodicCharacterPicture: EpisodicCharacterPicture;

	@OneToMany(
		(type) => EpisodicCharacterPicture,
		(episodicCharacterPicture) => episodicCharacterPicture.picture
	)
	episodicCharacterPictures: EpisodicCharacterPicture[];

	@OneToMany(
		(type) => CmChangePicture,
		(cmChangePicture) => cmChangePicture.picture
	)
	cmChangePictures: CmChangePicture[];

	@OneToMany(
		(type) => PrEpisodicPropPicture,
		(prEpisodicPropPicture) => prEpisodicPropPicture.picture
	)
	prEpisodicPropPictures: PrEpisodicPropPicture[];

	@OneToMany(
		(type) => SetPiecePicture,
		(setPiecePicture) => setPiecePicture.picture
	)
	setPiecePictures: SetPiecePicture[];

	@OneToMany((type) => HaLookPicture, (haLookPicture) => haLookPicture.picture)
	haLookPictures: HaLookPicture[];

	@OneToMany((type) => MuLookPicture, (muLookPicture) => muLookPicture.picture)
	muLookPictures: MuLookPicture[];

	@OneToOne(
		(type) => EpisodicActorPicture,
		(episodicActorPicture) => episodicActorPicture.picture
	)
	episodicActorPicture: EpisodicActorPicture;

	@OneToMany(
		(type) => PdMessagePicture,
		(pdMessagePicture) => pdMessagePicture.picture
	)
	pdMessagePictures: PdMessagePicture[];

	@OneToMany(
		() => MoneyTrackingPaymentPicture,
		(moneyTrackingPaymentPicture) => moneyTrackingPaymentPicture.picture
	)
	moneyTrackingPaymentPictures: MoneyTrackingPaymentPicture[];

	@OneToMany(
		() => SetMoneyTrackingPaymentPicture,
		(setMoneyTrackingPaymentPicture) => setMoneyTrackingPaymentPicture.picture
	)
	setMoneyTrackingPaymentPictures: SetMoneyTrackingPaymentPicture[];

	@OneToOne(
		(type) => EpisodicLogoPicture,
		(episodicLogoPicture) => episodicLogoPicture.picture
	)
	episodicLogoPicture: EpisodicLogoPicture;

	static rules() {
		return {
			...super.rules(),
			mobile_id: Joi.string().max(36).allow(null, ''),
			prod_id: Joi.number().integer().allow(null),
			episodic_id: Joi.number().integer().allow(null),
			file_name: Joi.string().max(100),
			description: Joi.string().max(1000).allow(null, ''),
			fitting_research: Joi.boolean(),
			department: Joi.number().integer(),
			resize_attempts: Joi.number().integer(),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer(),
			date_created: Joi.date().allow(null),
			created_by: Joi.number().integer(),
			needs_upload: Joi.boolean(),
			old_model_delete: Joi.boolean(),
		};
	}

	toJSON() {
		return {
			...super.toJSON(),
			mobile_id: this.mobile_id,
			prod_id: this.prod_id,
			episodic_id: this.episodic_id,
			file_name: this.file_name,
			description: this.description,
			fitting_research: this.fitting_research,
			department: this.department,
			resize_attempts: this.resize_attempts,
			last_updated_time: this.last_updated_time,
			last_updated_user: this.last_updated_user,
			date_created: this.date_created,
			created_by: this.created_by,
			needs_upload: this.needs_upload,
			old_model_delete: this.old_model_delete,
		};
	}

	assign(props?: GenPartial<Picture>) {
		super.assign(props);

		if (props) {
			if (typeof props.fitting_research !== 'undefined') {
				this.fitting_research = booleanTransformer.from(props.fitting_research);
			}
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
			if (typeof props.needs_upload !== 'undefined') {
				this.needs_upload = booleanTransformer.from(props.needs_upload);
			}
			if (typeof props.old_model_delete !== 'undefined') {
				this.old_model_delete = booleanTransformer.from(props.old_model_delete);
			}
		}
	}

	constructor(props?: GenPartial<Picture>) {
		super(props);
	}

	getUploadDate() {
		return this.date_created
			? this.date_created.local().format('MMMM DD, YYYY')
			: '';
	}
}
